import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, Radio } from "lucide-react";

const HERO_IMG = "/brand/hero-tower.jpg?v=4";
const HERO_IMG_SM = "/brand/hero-tower-sm.jpg?v=4";

// Count-up animation that fires when the stat scrolls into view
function CountUp({ target, suffix = "+", duration = 1600 }) {
  const ref = useRef(null);
  const [value, setValue] = useState(0);
  const startedRef = useRef(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !startedRef.current) {
            startedRef.current = true;
            const start = performance.now();
            const tick = (now) => {
              const elapsed = now - start;
              const progress = Math.min(elapsed / duration, 1);
              // ease-out cubic
              const eased = 1 - Math.pow(1 - progress, 3);
              setValue(Math.round(target * eased));
              if (progress < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
          }
        });
      },
      { threshold: 0.4 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [target, duration]);

  return (
    <span ref={ref}>
      {value}
      {suffix}
    </span>
  );
}

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[100svh] flex flex-col md:items-end overflow-hidden"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <picture>
          {/* Phones / portrait orientation get a tighter crop centered on the tower */}
          <source media="(max-width: 767px), (orientation: portrait)" srcSet={HERO_IMG_SM} />
          <img
            src={HERO_IMG}
            alt="Epicom self-supporting tower with PtP/PtMP dishes overlooking the Southern Alberta foothills"
            className="w-full h-full object-cover object-[60%_center] md:object-center"
            loading="eager"
            fetchpriority="high"
          />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-bg-primary via-transparent to-transparent" />
        {/* lattice grid */}
        <div className="absolute inset-0 bg-lattice-grid bg-lattice opacity-40" />
      </div>

      {/* Signal rings indicator - top right */}
      <div className="absolute top-32 right-12 hidden md:block">
        <div className="relative w-40 h-40">
          <div className="signal-rings">
            <span />
            <span />
            <span />
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-3 h-3 bg-signal rounded-full animate-signal-pulse" />
          </div>
        </div>
      </div>

      {/* Coordinates readout */}
      <div className="absolute top-28 left-0 right-0 z-10 pointer-events-none">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 font-mono text-[10px] tracking-[0.3em] text-steel-light/60 uppercase">
          <div>50.0452° N · 110.6766° W</div>
          <div className="text-signal mt-1 flex items-center gap-2">
            <span className="inline-block w-4 h-[1px] bg-signal" />
            Southern Alberta Corridor
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 lg:px-10 pb-12 md:pb-36 lg:pb-32 pt-44 md:pt-40 flex-1 flex flex-col justify-center">
        <div className="max-w-5xl">
          <div className="flex items-center gap-3 mb-8 animate-slide-up">
            <Radio className="text-signal" size={18} />
            <span
              className="font-mono text-xs tracking-[0.2em] uppercase text-signal"
              data-testid="hero-overline"
            >
              Wireless · Networking · Cell Boosters · Towers · DAS · Security
            </span>
          </div>

          <h1
            data-testid="hero-heading"
            className="heading text-[clamp(2.25rem,11vw,3rem)] sm:text-6xl md:text-7xl lg:text-8xl font-black uppercase text-white leading-[0.9] mb-8 animate-slide-up"
            style={{ animationDelay: "120ms" }}
          >
            CONNECTING
            <br />
            WHAT
            <br />
            <span className="text-signal">MATTERS.</span>
          </h1>

          <p
            data-testid="hero-subtitle"
            className="text-steel-light text-base sm:text-lg md:text-xl max-w-2xl leading-relaxed mb-10 animate-slide-up"
            style={{ animationDelay: "240ms" }}
          >
            Point-to-point &amp; point-to-multipoint wireless, home and small
            business networking, cell repeating &amp; DAS systems, and
            small-to-medium self-supporting tower builds — plus custom
            communications infrastructure that turns grain legs, silos, and
            outbuildings into practical communication towers.
          </p>

          <div
            className="flex flex-wrap items-center gap-3 sm:gap-4 animate-slide-up"
            style={{ animationDelay: "360ms" }}
          >
            <a
              href="#contact"
              data-testid="hero-cta-quote"
              className="group inline-flex items-center gap-2 sm:gap-3 bg-signal hover:bg-signal-hover text-white font-bold uppercase tracking-wider text-xs sm:text-sm md:text-base px-5 py-3 sm:px-6 sm:py-4 md:px-8 md:py-5 border-2 border-transparent hover:border-white/20 transition-all"
            >
              Request a Quote
              <ArrowRight
                size={16}
                className="group-hover:translate-x-1 transition-transform sm:w-[18px] sm:h-[18px]"
              />
            </a>
            <a
              href="#services"
              data-testid="hero-cta-services"
              className="inline-flex items-center gap-2 sm:gap-3 bg-transparent text-white font-bold uppercase tracking-wider text-xs sm:text-sm md:text-base px-5 py-3 sm:px-6 sm:py-4 md:px-8 md:py-5 border-2 border-steel hover:border-signal hover:bg-signal/10 transition-all"
            >
              Explore Services
            </a>
          </div>
        </div>
      </div>

      {/* Bottom stat strip — normal flow on mobile, absolute on md+ */}
      <div className="relative md:absolute md:bottom-0 left-0 right-0 border-y border-steel-dark bg-bg-primary/80 backdrop-blur-sm z-10 group/stats">
        {/* signal sweep line */}
        <div className="pointer-events-none absolute top-0 left-0 right-0 h-px overflow-hidden">
          <div className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-signal to-transparent animate-scan-line" />
        </div>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4">
          {[
            { k: "PtP / PtMP LINKS", v: 200 },
            { k: "UNIFI DEPLOYMENTS", v: 40 },
            { k: "CELL BOOSTERS / DAS", v: 100 },
            { k: "SELF-SUPPORTING TOWERS", v: 20 },
          ].map((s, i) => (
            <div
              key={s.k}
              data-testid={`hero-stat-${s.k.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="relative py-6 px-6 border-r border-steel-dark last:border-r-0 flex flex-col items-start justify-center transition-colors duration-300 hover:bg-signal/5 animate-slide-up"
              style={{ animationDelay: `${480 + i * 120}ms`, animationFillMode: "both" }}
            >
              {/* signal pulse dot */}
              <span className="absolute top-3 right-3 flex items-center gap-1.5">
                <span className="font-mono text-[9px] tracking-[0.3em] text-steel-light/40 uppercase">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="relative flex w-1.5 h-1.5">
                  <span className="absolute inset-0 rounded-full bg-signal opacity-60 animate-ping" />
                  <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-signal" />
                </span>
              </span>

              <div className="heading text-3xl md:text-4xl text-white tabular-nums tracking-tight transition-all duration-300 group-hover/stats:text-steel-light/70 hover:!text-signal hover:drop-shadow-[0_0_18px_rgba(255,90,40,0.5)]">
                <CountUp target={s.v} />
              </div>
              <div className="font-mono text-[10px] md:text-xs tracking-[0.25em] text-steel-light uppercase mt-1">
                {s.k}
              </div>
              {/* underline accent on hover */}
              <span className="absolute left-6 right-6 bottom-0 h-px bg-signal scale-x-0 origin-left transition-transform duration-500 hover:scale-x-100" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
